import { useQuery } from '@tanstack/react-query';
import { ErrorCode, type ApiErrorBody, type ChatQuickReplyKey, type ChatView } from '@brimo/shared';
import { ApiError, GENERIC_ERROR_MESSAGE } from '../../lib/api';
import type {
  PortalActivation,
  PortalCodeChallenge,
  PortalDocumentFilter,
  PortalDocuments,
  PortalLoginSession,
  PortalMagicView,
  PortalMe,
  PortalOverview,
  PortalProfile,
  PortalProfileChange,
  PortalPendingChange,
  PortalPublicOrganization,
  PortalTicketCard,
  PortalTicketDetail,
  PortalUploadResult,
} from './types';

/**
 * Client of the portal routes: `/api/public/portal` (no session) and `/api/portal` (the `__Host-portal` cookie set by
 * the API). The portal never touches the workspace session — its 401s must not trigger the workspace's
 * „session ended" event — so it has its own fetch wrapper. A 401 `PORTAL_UNAUTHENTICATED` sends the page to the
 * organization's login (`usePortalGuard` in `frame/PortalLayout`).
 */

async function parse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function errorOf(status: number, json: unknown): ApiError {
  const body = (json as Partial<ApiErrorBody> | null)?.error ?? {
    code: ErrorCode.INTERNAL,
    message: GENERIC_ERROR_MESSAGE,
  };
  return new ApiError(status, body);
}

interface RequestInitPortal {
  method?: 'GET' | 'POST';
  body?: unknown;
  formData?: FormData;
  signal?: AbortSignal | undefined;
}

export async function portalRequest<T>(path: string, init: RequestInitPortal = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (init.formData) body = init.formData;
  else if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.body);
  }
  const response = await fetch(`/api${path}`, {
    method: init.method ?? (body === undefined ? 'GET' : 'POST'),
    headers,
    body: body ?? null,
    credentials: 'same-origin',
    signal: init.signal ?? null,
  });
  const json = await parse(response);
  if (!response.ok) throw errorOf(response.status, json);
  return json as T;
}

/** True for the portal's „not signed in" answer. */
export function isPortalUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.code === ErrorCode.PORTAL_UNAUTHENTICATED;
}

/** Do not retry client errors; the portal frame reacts to 401/403 itself. */
function retryServerErrors(count: number, error: unknown): boolean {
  return !(error instanceof ApiError && error.status < 500) && count < 1;
}

// --- public: magic link, login ------------------------------------------------------------------------------

const magicBase = (token: string) => `/public/portal/magic/${encodeURIComponent(token)}`;

export const portalMagicKey = (token: string) => ['portal', 'magic', token] as const;

export function usePortalMagic(token: string) {
  return useQuery({
    queryKey: portalMagicKey(token),
    queryFn: ({ signal }) => portalRequest<PortalMagicView>(magicBase(token), { signal }),
    retry: retryServerErrors,
    // Every visit asks again: the state moves on while the customer fills the form behind the link. Each answer
    // in state `self_disclosure` rotates the form's token, so the page does not refetch on focus.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

export function setPortalPassword(token: string, password: string, passwordRepeat: string) {
  return portalRequest<PortalActivation>(`${magicBase(token)}/password`, {
    body: { password, passwordRepeat },
  });
}

export function resendMagicLink(token: string) {
  return portalRequest<{ maskedEmail: string }>('/public/portal/magic/resend', { body: { token } });
}

export const portalOrganizationKey = (slug: string) => ['portal', 'organization', slug] as const;

export function usePortalOrganization(slug: string) {
  return useQuery({
    queryKey: portalOrganizationKey(slug),
    queryFn: ({ signal }) =>
      portalRequest<PortalPublicOrganization>(`/public/portal/${encodeURIComponent(slug)}`, { signal }),
    retry: retryServerErrors,
    staleTime: 5 * 60_000,
  });
}

const loginBase = (slug: string) => `/public/portal/${encodeURIComponent(slug)}`;

export function portalLogin(slug: string, body: { email: string; password: string; remember: boolean }) {
  return portalRequest<PortalLoginSession | PortalCodeChallenge>(`${loginBase(slug)}/login`, { body });
}

export function portalLoginCode(slug: string, challengeId: string, code: string) {
  return portalRequest<PortalLoginSession>(`${loginBase(slug)}/login/code`, { body: { challengeId, code } });
}

export function portalResendCode(slug: string, challengeId: string) {
  return portalRequest<PortalCodeChallenge>(`${loginBase(slug)}/login/code/resend`, {
    body: { challengeId },
  });
}

export function portalPasswordForgot(slug: string, email: string) {
  return portalRequest<{ ok: true }>(`${loginBase(slug)}/password-forgot`, { body: { email } });
}

// --- portal session -----------------------------------------------------------------------------------------

export const portalKeys = {
  me: ['portal', 'me'] as const,
  overview: ['portal', 'overview'] as const,
  documents: (filter: PortalDocumentFilter) => ['portal', 'documents', filter] as const,
  documentsRoot: ['portal', 'documents'] as const,
  profile: ['portal', 'profile'] as const,
  chat: ['portal', 'chat'] as const,
  tickets: ['portal', 'tickets'] as const,
  ticket: (id: string) => ['portal', 'tickets', id] as const,
};

/** Portal pages refetch when the window regains focus and after their own mutations (no SSE in the portal). */
const portalQueryOptions = {
  retry: retryServerErrors,
  refetchOnWindowFocus: true,
  staleTime: 10_000,
} as const;

export function usePortalMe() {
  return useQuery({
    queryKey: portalKeys.me,
    queryFn: ({ signal }) => portalRequest<PortalMe>('/portal/me', { signal }),
    ...portalQueryOptions,
  });
}

export function usePortalOverview() {
  return useQuery({
    queryKey: portalKeys.overview,
    queryFn: ({ signal }) => portalRequest<PortalOverview>('/portal/overview', { signal }),
    ...portalQueryOptions,
  });
}

export function usePortalDocuments(filter: PortalDocumentFilter) {
  return useQuery({
    queryKey: portalKeys.documents(filter),
    queryFn: ({ signal }) => portalRequest<PortalDocuments>(`/portal/documents?filter=${filter}`, { signal }),
    ...portalQueryOptions,
  });
}

export const portalDocumentHref = (id: string) => `/api/portal/documents/${encodeURIComponent(id)}/file`;

export function usePortalProfile() {
  return useQuery({
    queryKey: portalKeys.profile,
    queryFn: ({ signal }) => portalRequest<PortalProfile>('/portal/profile', { signal }),
    ...portalQueryOptions,
  });
}

export function submitProfileChanges(changes: PortalProfileChange[]) {
  return portalRequest<{ pending: PortalPendingChange[] }>('/portal/profile/changes', { body: { changes } });
}

export function changePortalPassword(current: string, next: string) {
  return portalRequest<null>('/portal/password', { body: { current, next } });
}

export function portalLogout() {
  return portalRequest<null>('/portal/logout', { method: 'POST' });
}

// --- chat and tickets ---------------------------------------------------------------------------------------

export function usePortalChat(enabled = true) {
  return useQuery({
    queryKey: portalKeys.chat,
    queryFn: ({ signal }) => portalRequest<ChatView>('/portal/chat', { signal }),
    enabled,
    ...portalQueryOptions,
  });
}

export function sendChatMessage(input: { text: string } | { quickReply: ChatQuickReplyKey }) {
  return portalRequest<ChatView>('/portal/chat/messages', { body: input });
}

export function requestChatAppointment(eventTypeId: string, startsAt: string) {
  return portalRequest<ChatView>('/portal/chat/appointment', { body: { eventTypeId, startsAt } });
}

export function cancelChatAppointment(appointmentId: string) {
  return portalRequest<ChatView>(`/portal/chat/appointments/${encodeURIComponent(appointmentId)}/cancel`, {
    method: 'POST',
  });
}

export const chatAppointmentIcsHref = (appointmentId: string) =>
  `/api/portal/chat/appointments/${encodeURIComponent(appointmentId)}/ics`;

export function uploadChatAttachment(file: File) {
  const form = new FormData();
  form.append('file', file);
  return portalRequest<{ fileId: string; chat: ChatView }>('/portal/chat/attachment', { formData: form });
}

export function usePortalTickets() {
  return useQuery({
    queryKey: portalKeys.tickets,
    queryFn: ({ signal }) => portalRequest<{ items: PortalTicketCard[] }>('/portal/tickets', { signal }),
    ...portalQueryOptions,
  });
}

export function usePortalTicket(id: string | null) {
  return useQuery({
    queryKey: portalKeys.ticket(id ?? ''),
    queryFn: ({ signal }) =>
      portalRequest<PortalTicketDetail>(`/portal/tickets/${encodeURIComponent(id ?? '')}`, { signal }),
    enabled: id !== null,
    ...portalQueryOptions,
  });
}

export function replyToTicket(id: string, text: string, files: File[]) {
  const form = new FormData();
  form.append('text', text);
  for (const file of files) form.append('files', file);
  return portalRequest<PortalTicketDetail>(`/portal/tickets/${encodeURIComponent(id)}/messages`, {
    formData: form,
  });
}

export const ticketFileHref = (ticketId: string, fileId: string) =>
  `/api/portal/tickets/${encodeURIComponent(ticketId)}/files/${encodeURIComponent(fileId)}`;

// --- document upload with progress (Portal 18 „wird geladen", „Abbrechen") ------------------------------------

export interface UploadHandle {
  promise: Promise<PortalUploadResult>;
  abort: () => void;
}

export class UploadAbortedError extends Error {
  constructor() {
    super('aborted');
    this.name = 'UploadAbortedError';
  }
}

/** `POST /api/portal/documents` through `XMLHttpRequest`, so the row can show the progress and abort. */
export function uploadPortalDocument(
  file: File,
  requestId: string | null,
  onProgress: (fraction: number) => void,
): UploadHandle {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<PortalUploadResult>((resolve, reject) => {
    xhr.open('POST', '/api/portal/documents');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.withCredentials = true;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      let json: unknown = null;
      try {
        json = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : null;
      } catch {
        json = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(json as PortalUploadResult);
      else reject(errorOf(xhr.status, json));
    };
    xhr.onerror = () => reject(new ApiError(0, { code: ErrorCode.INTERNAL, message: GENERIC_ERROR_MESSAGE }));
    xhr.onabort = () => reject(new UploadAbortedError());
    const form = new FormData();
    // Fields before the file: the multipart parser reads `requestId` before it streams the file part.
    if (requestId) form.append('requestId', requestId);
    form.append('file', file);
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}
